import { Card } from "@prisma/ui";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { HonestKind } from "../app/navigation";

/**
 * Honest capability state (locked decision 10; decision O10). Nothing here is clickable as if
 * it worked and nothing is simulated: a status line in the locked wording and one sentence on
 * what will appear.
 */
export function HonestState({
  icon: Icon,
  kind,
  descriptionKey,
}: {
  icon: LucideIcon;
  kind: HonestKind;
  descriptionKey: string;
}) {
  const { t } = useTranslation();
  const status = kind === "ai_production_phase" ? t("honest.awaiting_ai_production_phase") : t("honest.arrives_with_workflow");
  return (
    <Card className="relative overflow-hidden p-10" role="status">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[image:var(--gradient-signature)] opacity-60"
      />
      <div className="flex items-start gap-6">
        <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-[var(--radius-card)] bg-[var(--accent-primary-soft)] text-violet">
          <Icon aria-hidden="true" className="size-7" strokeWidth={1.6} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-line bg-elevated px-3 py-1 text-[12px] font-semibold uppercase tracking-wide text-ink">
              {kind === "ai_production_phase" ? t("honest.capability_not_enabled") : status}
            </span>
            {kind === "ai_production_phase" && (
              <span className="inline-flex items-center rounded-full bg-[var(--accent-secondary-soft)] px-3 py-1 text-[12px] font-semibold uppercase tracking-wide text-magenta-text">
                {status}
              </span>
            )}
          </div>
          <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink">{t(descriptionKey)}</p>
          <p className="mt-2 max-w-2xl text-[14px] text-ink-2">{t("honest.nothing_here_is_simulated")}</p>
        </div>
      </div>
    </Card>
  );
}
